// Phase C · Aggiungi una NUOVA sottoriga misura alla stessa voce padre.
// Avvia una variazione cliente positiva (motore 5C certificato). U.M. e prezzo READ-ONLY dal padre.
// Preview = nessuna scrittura. Alla conferma la variazione diventa EFFICACE e crea la measure-line.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { salApi } from '../../lib/api';
import { toast } from 'sonner';
import { PlusCircle, AlertTriangle, Loader2, Info } from 'lucide-react';
import { fmtQty, fmtEur, parseNum } from '../../lib/formatQty';

const uuid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `op-${Date.now()}-${Math.random().toString(16).slice(2)}`);

export const NewMeasureLineDialog = ({ open, onOpenChange, item, onDone }) => {
  const unit = item?.unit || '';
  const [description, setDescription] = useState('');
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const opKeyRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    opKeyRef.current = uuid();
    setDescription(''); setQuantity(''); setReason('');
    setPreview(null); setPreviewError(null); setSubmitting(false);
  }, [open]);

  const qty = parseNum(quantity);

  const fetchPreview = useCallback(async () => {
    if (isNaN(qty) || qty <= 0 || !description.trim()) { setPreview(null); setPreviewError(null); return; }
    const reasonStub = reason.trim().length >= 6 ? reason.trim() : 'anteprima__________';
    setLoading(true);
    try {
      const res = await salApi.previewMeasurementLineAdd(item.id, {
        description: description.trim(), quantity: qty, reason: reasonStub,
      });
      const d = res.data;
      setPreview({ unitPrice: d.contract_unit_price_net, net: d.net_delta,
        parentBefore: d.current_quantity_before, parentAfter: d.proposed_current_quantity });
      setPreviewError(null);
    } catch (e) {
      const detail = e?.response?.data?.detail || {};
      setPreview(null);
      setPreviewError(detail.message || 'Anteprima non disponibile');
    } finally { setLoading(false); }
  }, [qty, description, reason, item?.id]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(fetchPreview, 300);
    return () => clearTimeout(t);
  }, [open, quantity, description, fetchPreview]);

  const canConfirm = preview && !previewError && !loading && reason.trim().length >= 6 && qty > 0 && description.trim() && !submitting;

  const confirm = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    try {
      await salApi.applyMeasurementLineAdd(item.id, {
        description: description.trim(), quantity: qty, reason: reason.trim(), idempotency_key: opKeyRef.current,
      });
      toast.success('Nuova misura aggiunta: variazione contrattuale registrata.');
      onOpenChange(false);
      onDone && onDone();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Errore durante l\'aggiunta della misura'));
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="new-measure-line-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#3C3C3C]">
            <PlusCircle className="w-4 h-4 text-[#FF7819]" /> Aggiungi nuova misura
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="text-xs text-[#737373]">{item.code} · {item.description}</div>
          <div className="flex items-start gap-2 text-[11px] text-[#737373] bg-[#fafafa] rounded p-2" data-testid="new-measure-guard">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            Usa questa funzione solo per una nuova misura della STESSA lavorazione, con stessa U.M. e stesso prezzo unitario.
            Se natura tecnica, U.M. o prezzo sono diversi, usa un Extra NEW-WORK.
          </div>
          <div>
            <Label className="text-xs">Descrizione nuova misura</Label>
            <Input type="text" value={description} onChange={(e) => setDescription(e.target.value)}
              className="h-9 input-abitalo mt-1" placeholder="Es. Bagno 4" data-testid="new-measure-desc-input" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Quantità</Label>
              <Input type="text" inputMode="decimal" value={quantity} onChange={(e) => setQuantity(e.target.value)}
                className="h-9 input-abitalo mt-1" placeholder="0" data-testid="new-measure-qty-input" />
            </div>
            <div>
              <Label className="text-xs">U.M.</Label>
              <Input value={unit} readOnly disabled className="h-9 input-abitalo mt-1 bg-[#f5f5f5]" data-testid="new-measure-unit" />
            </div>
            <div>
              <Label className="text-xs">Prezzo unit.</Label>
              <Input value={preview ? fmtEur(preview.unitPrice) : (item.contract_unit_price_net != null ? fmtEur(item.contract_unit_price_net) : '—')}
                readOnly disabled className="h-9 input-abitalo mt-1 bg-[#f5f5f5]" data-testid="new-measure-price" />
            </div>
          </div>
          {loading && <div className="flex items-center gap-2 text-xs text-[#737373]"><Loader2 className="w-3 h-3 animate-spin" /> Calcolo impatto…</div>}
          {previewError && !loading && (
            <div className="flex items-start gap-2 text-xs text-red-600 bg-red-50 rounded p-2" data-testid="new-measure-error">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /> {previewError}
            </div>
          )}
          {preview && !loading && !previewError && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50/50 p-3 space-y-1" data-testid="new-measure-preview">
              <Row label="Voce padre (prima → dopo)" value={`${fmtQty(preview.parentBefore)} → ${fmtQty(preview.parentAfter)} ${unit}`} />
              <div className="border-t border-[#e5e5e5] my-1" />
              <Row label="Valore variazione" value={`+ ${fmtEur(Math.abs(preview.net))}`} strong strongClass="text-emerald-700" testid="new-measure-value" />
            </div>
          )}
          <div>
            <Label className="text-xs">Motivazione (obbligatoria)</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
              className="mt-1 input-abitalo text-sm" placeholder="Motivo dell'aggiunta (min 6 caratteri)"
              data-testid="new-measure-reason-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="new-measure-cancel-btn">Annulla</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={!canConfirm} onClick={confirm} data-testid="new-measure-confirm-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            Conferma
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ label, value, strong, strongClass, testid }) => (
  <div className="flex items-center justify-between text-xs">
    <span className="text-[#737373]">{label}</span>
    <span className={strong ? `font-bold ${strongClass || 'text-[#3C3C3C]'}` : 'text-[#3C3C3C]'} data-testid={testid}>{value}</span>
  </div>
);

export default NewMeasureLineDialog;
